import { TranslatePipe } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormGroup, FormGroup, FormControl, Validators, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { postcodeValidator } from '../../util/address/util';
import { getErrorMessage } from '../../util/state/error-handling';
import { debounceTime, Subject, takeUntil } from 'rxjs';

/** The details being returned from the form */
export interface CustomerDetails {
	address: string;
	addressPostcodeLookup?: string;
	addressesSameCheck: boolean;
	contactNumber: string;
	deliveryAddress: string;
	deliveryAddressPostcodeLookup?: string;
	emailAddress: string;
	firstName: string;
	lastMame: string;

	// Optional address fields
	addressLine1?: string;
	addressLine2?: string;
	city?: string;
	postcode?: string;
	country?: string;

	// Optional delivery address fields
	deliveryAddressLine1?: string;
	deliveryAddressLine2?: string;
	deliveryCity?: string;
	deliveryPostcode?: string;
	deliveryCountry?: string;

	// International address booleans
	isInternationalAddress?: boolean;
	isInternationalDeliveryAddress?: boolean;
}

export interface FormStatus {
	valid: boolean;
	value: CustomerDetails;
	errors: Record<string, any>[];
}
/**
 * Customer details form that collects name and contact details for a customer.
 */
@Component({
	selector: 'ui-customer-details-form',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslatePipe],
	templateUrl: './customer-details-form.component.html',
	styleUrl: './customer-details-form.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDetailsFormComponent implements OnInit, OnDestroy {
	/** Values to pre-populate the form */
	private _customerDetails: CustomerDetails | undefined = undefined;

	@Input()
	set customerDetails(value: CustomerDetails) {
		this._customerDetails = value;
		this.populateForm();
	}
	get customerDetails(): CustomerDetails | undefined {
		return this._customerDetails;
	}
	/** List of addresses to display in the address dropdown */
	@Input() addresses: string[] = [];
	/** List of delivery addresses to display in the delivery address dropdown */
	@Input() deliveryAddresses: string[] = [];
	/** Object containing hits for the form fields. Key representing the control name, value the hint text */
	@Input() fieldHints: Record<string, string> = {};
	/** Whether to display admin only ontent */
	@Input() adminJourney = false;
	/** Whether to display the delivery address section */
	@Input() disableDelivery = false;
	/** When true, render the "enter manually" question as a single checkbox instead of yes/no radios */
	@Input() useCheckboxForManualAddress = false;

	/** When this valusd is changed to true re-run the form validation */
	@Input()
	get shouldTriggerValidation(): boolean {
		return this._shouldTriggerValidation;
		return this._shouldTriggerValidation;
	}
	set shouldTriggerValidation(shouldTriggerValidation: boolean) {
		this._shouldTriggerValidation = shouldTriggerValidation || false;
		console.log('here', this._shouldTriggerValidation);
		if (this._shouldTriggerValidation) {
			this.customerDetailsForm.updateValueAndValidity();
		}
		this.checkPostcodeValidation();
	}
	private _shouldTriggerValidation = false;

	/** Emits a postcode when entered into the postcode lookup */
	@Output() postcodeLookup = new EventEmitter<string>();
	/** Emits a delivery postcode when entered into the delivery postcode lookup */
	@Output() deliveryPostcodeLookup = new EventEmitter<string>();
	/** Emits the status of the form on form changes */
	@Output() customerDetailsChange = new EventEmitter<FormStatus>();

	destroy$$ = new Subject<void>();
	showAddressesSameCheck = false;
	showDeliveryAddress = false;
	isInternationalAddress = false;
	isInternationalDeliveryAddress = false;
	isSubmitted = false;
	showAddressError = false;
	showDeliveryAddressError = false;

	customerDetailsForm: FormGroup = new FormGroup({
		firstName: new FormControl('', Validators.required),
		lastName: new FormControl('', Validators.required),
		emailAddress: new FormControl('', [Validators.required, Validators.email]),
		contactNumber: new FormControl('', [
			Validators.required,
			Validators.minLength(9),
			Validators.maxLength(15),
			Validators.pattern(/[0-9]/),
		]),
		addressPostcodeLookup: new FormControl('', [
			postcodeValidator(),
			Validators.required,
		]),
		address: new FormControl('', [Validators.required]),
		deliveryAddressPostcodeLookup: new FormControl('', [
			postcodeValidator(),
			Validators.required,
		]),
		deliveryAddress: new FormControl('', Validators.required),
		addressesSameCheck: new FormControl('', Validators.required),
		isInternationalAddress: new FormControl('', Validators.required),
		isInternationalDeliveryAddress: new FormControl('', Validators.required),
	});

	addressControl = this.customerDetailsForm.get('address');
	deliveryAddressControl = this.customerDetailsForm.get('deliveryAddress');

	checkPostcodeValidation() {
		this.showAddressError =
			this.shouldTriggerValidation &&
			!!this.addressControl &&
			this.addressControl.invalid;

		console.log('show address errror', this.showAddressError);

		this.showDeliveryAddressError =
			this.shouldTriggerValidation &&
			!!this.deliveryAddressControl &&
			this.deliveryAddressControl.invalid;

		console.log('show address errror', this.showAddressError);
	}

	get form() {
		return this.customerDetailsForm.controls;
	}

	isAddressValid = this.customerDetailsForm.get('address')?.valid;

	completeInternationalAddress(): boolean {
		return !!(
			this.customerDetailsForm.get('addressLine1')?.value &&
			this.customerDetailsForm.get('city')?.value &&
			this.customerDetailsForm.get('postcode')?.value &&
			this.customerDetailsForm.get('country')?.value
		);
	}

	setShowSameAddressCheck() {
		if (this.isInternationalAddress) {
			console.log('setting show same,, is international');
			console.log('complete addre4ss', this.completeInternationalAddress());
			this.showAddressesSameCheck = this.completeInternationalAddress();
		} else {
			const address = this.customerDetailsForm.get('address');

			if (address) {
				this.showAddressesSameCheck = !!address.value;
			}
		}
	}

	ngOnInit(): void {
		if (this.adminJourney) {
			this.customerDetailsForm.addControl(
				'contactNumberConsent',
				new FormControl('', Validators.requiredTrue)
			);
			this.customerDetailsForm.addControl(
				'emailAddressConsent',
				new FormControl('', Validators.requiredTrue)
			);
		}

		this.populateForm();

		this.setShowSameAddressCheck();

		const addressesSameCheckControl =
			this.customerDetailsForm.get('addressesSameCheck');
		if (addressesSameCheckControl) {
			addressesSameCheckControl.valueChanges
				.pipe(takeUntil(this.destroy$$))
				.subscribe((value) => {
					this.showDeliveryAddress = value === 'false';

					if (this.showDeliveryAddress) {
						console.log(
							addressesSameCheckControl,
							addressesSameCheckControl.dirty,
							addressesSameCheckControl.touched
						);
						if (addressesSameCheckControl.touched) {
							// dont do this on initial load
							this.setDeliveryAddress(true);
						}
					} else {
						this.setDeliveryAddress(false);
						this.deliveryAddresses = this.addresses;
					}
				});
		}

		this.customerDetailsForm.valueChanges
			.pipe(debounceTime(600), takeUntil(this.destroy$$))
			.subscribe(() => {
				this.checkPostcodeValidation();
				this.concatinateAddressLines();
				this.setShowSameAddressCheck();
				const errors: Record<string, any>[] = [];
				Object.keys(this.customerDetailsForm.controls).forEach((key) => {
					const controlErrors = this.customerDetailsForm.get(key)?.errors;
					if (controlErrors) {
						errors.push({ control: key, errors: controlErrors });
					}
				});
				this.customerDetailsChange.emit({
					valid: this.customerDetailsForm.valid,
					errors: errors,
					value: this.customerDetailsForm.value,
				});
			});

		this.customerDetailsForm
			.get('isInternationalAddress')
			?.valueChanges.pipe(takeUntil(this.destroy$$))
			.subscribe((value) => {
				this.isInternationalAddress = value === 'true';
				this.setIsInternationalAddress();
			});

		this.customerDetailsForm
			.get('isInternationalDeliveryAddress')
			?.valueChanges.pipe(takeUntil(this.destroy$$))
			.subscribe((value) => {
				this.isInternationalDeliveryAddress = value === 'true';
				this.setIsInternationalDeliveryAddress();
			});
	}

	ngOnDestroy(): void {
		this.destroy$$.next();
		this.destroy$$.complete();
	}

	setIsInternationalAddress() {
		if (this.isInternationalAddress) {
			this.customerDetailsForm.removeControl('addressPostcodeLookup');
			this.addresses = [];
			this.customerDetailsForm.get('address')?.setValue('');

			this.customerDetailsForm.addControl(
				'addressLine1',
				new FormControl('', [Validators.required])
			);
			this.customerDetailsForm.addControl(
				'addressLine2',
				new FormControl('', [])
			);
			this.customerDetailsForm.addControl(
				'city',
				new FormControl('', [Validators.required])
			);
			this.customerDetailsForm.addControl(
				'postcode',
				new FormControl('', [])
			);
			this.customerDetailsForm.addControl(
				'country',
				new FormControl('', [Validators.required])
			);
		} else {
			this.customerDetailsForm.removeControl('addressLine1');
			this.customerDetailsForm.removeControl('addressLine2');
			this.customerDetailsForm.removeControl('city');
			this.customerDetailsForm.removeControl('postcode');
			this.customerDetailsForm.removeControl('country');

			this.customerDetailsForm.get('address')?.setValue('');

			this.customerDetailsForm.addControl(
				'addressPostcodeLookup',
				new FormControl('', [postcodeValidator(), Validators.required])
			);
		}
		this.setShowSameAddressCheck();
	}

	setIsInternationalDeliveryAddress() {
		if (this.isInternationalDeliveryAddress) {
			this.customerDetailsForm.get('deliveryAddress')?.setValue('');
			this.deliveryAddresses = [];
			this.customerDetailsForm.removeControl('deliveryAddressPostcodeLookup');

			this.customerDetailsForm.addControl(
				'deliveryAddressLine1',
				new FormControl('', [Validators.required])
			);
			this.customerDetailsForm.addControl(
				'deliveryAddressLine2',
				new FormControl('', [])
			);
			this.customerDetailsForm.addControl(
				'deliveryCity',
				new FormControl('', [Validators.required])
			);
			this.customerDetailsForm.addControl(
				'deliveryPostcode',
				new FormControl('', [])
			);
			this.customerDetailsForm.addControl(
				'deliveryCountry',
				new FormControl('', [Validators.required])
			);
		} else {
			this.customerDetailsForm.removeControl('deliveryAddressLine1');
			this.customerDetailsForm.removeControl('deliveryAddressLine2');
			this.customerDetailsForm.removeControl('deliveryCity');
			this.customerDetailsForm.removeControl('deliveryPostcode');
			this.customerDetailsForm.removeControl('deliveryCountry');

			this.customerDetailsForm.get('deliveryAddress')?.setValue('');

			this.customerDetailsForm.addControl(
				'deliveryAddressPostcodeLookup',
				new FormControl('', [postcodeValidator, Validators.required])
			);
		}
	}

	setDeliveryAddress(clearDeliveryAddress = false) {
		if (clearDeliveryAddress) {
			console.log('clearing delivery address');
			this.isInternationalDeliveryAddress = false;
			this.setIsInternationalDeliveryAddress();
			const deliveryAddressPostcodeLookupControl = this.customerDetailsForm.get(
				'deliveryAddressPostcodeLookup'
			);
			if (deliveryAddressPostcodeLookupControl) {
				deliveryAddressPostcodeLookupControl.setValue('');
				deliveryAddressPostcodeLookupControl.setValidators([
					postcodeValidator,
					Validators.required,
				]);
			}
			const deliveryAddressControl =
				this.customerDetailsForm.get('deliveryAddress');
			if (deliveryAddressControl) {
				deliveryAddressControl.setValue('');
			}
			this.deliveryAddresses = [];
		} else {
			if (this.isInternationalAddress) {
				this.isInternationalDeliveryAddress = true;
				this.setIsInternationalDeliveryAddress();
				this.customerDetailsForm
					.get('deliveryAddressLine1')
					?.setValue(this.customerDetailsForm.get('addressLine1')?.value);
				this.customerDetailsForm
					.get('deliveryAddressLine2')
					?.setValue(this.customerDetailsForm.get('addressLine2')?.value);
				this.customerDetailsForm
					.get('deliveryCity')
					?.setValue(this.customerDetailsForm.get('city')?.value);
				this.customerDetailsForm
					.get('deliveryPostcode')
					?.setValue(this.customerDetailsForm.get('postcode')?.value);
				this.customerDetailsForm
					.get('deliveryCountry')
					?.setValue(this.customerDetailsForm.get('country')?.value);
			} else {
				this.customerDetailsForm
					.get('deliveryAddressPostcodeLookup')
					?.setValue(
						this.customerDetailsForm.get('addressPostcodeLookup')?.value
					);
				this.customerDetailsForm
					.get('deliveryAddressPostcodeLookup')
					?.setValidators([]);
				this.customerDetailsForm
					.get('deliveryAddress')
					?.setValue(this.customerDetailsForm.get('address')?.value);
			}
		}
	}

	populateForm() {
		this.customerDetailsForm.get('isInternationalAddress')?.setValue('false');
		this.customerDetailsForm
			.get('isInternationalDeliveryAddress')
			?.setValue('false');

		if (this.customerDetails) {
			console.log(this.customerDetailsForm);
			this.customerDetailsForm.patchValue(this.customerDetails, {
				emitEvent: true,
			});
			console.log(this.customerDetailsForm);
		}
	}

	showErrors(field: string): boolean {
		const control = this.customerDetailsForm.get(field);
		if (this._shouldTriggerValidation) {
			return !!control && control.invalid;
		} else {
			return !!control && control.invalid && (control.dirty || control.touched);
		}
	}

	getErrorMessage(errorKey: string, field: string): string {
		return getErrorMessage(
			this.customerDetailsForm.get(field)?.errors || {},
			errorKey
		);
	}

	onManualAddressCheckboxChange(event: Event) {
		const checked = (event.target as HTMLInputElement).checked;
		this.customerDetailsForm
			.get('isInternationalAddress')
			?.setValue(checked ? 'true' : 'false');
	}

	onManualDeliveryAddressCheckboxChange(event: Event) {
		const checked = (event.target as HTMLInputElement).checked;
		this.customerDetailsForm
			.get('isInternationalDeliveryAddress')
			?.setValue(checked ? 'true' : 'false');
	}

	// Number input text validation, keycode 8 is backspacev
	keyPressNumberField(event: any) {
		const pattern = /[0-9\+\-\ ]/;
		const inputChar = String.fromCharCode(event.charCode);
		const inputElement = event.target as HTMLInputElement;
		if (
			(event.keyCode != 8 && !pattern.test(inputChar)) ||
			inputElement.value.length >= 15
		) {
			event.preventDefault();
		}
	}

	concatinateAddressLines() {
		let addressString = '';
		let deliveryAddressString = '';

		if (this.isInternationalAddress) {
			const addressLine1 =
				this.customerDetailsForm.get('addressLine1')?.value || '';
			const addressLine2 =
				this.customerDetailsForm.get('addressLine2')?.value || '';
			const city = this.customerDetailsForm.get('city')?.value || '';
			const postcode = this.customerDetailsForm.get('postcode')?.value || '';
			const country = this.customerDetailsForm.get('country')?.value || '';
			addressString = [addressLine1, addressLine2, city, postcode, country]
				.filter((part) => part)
				.join(', ');
		}

		if (this.isInternationalDeliveryAddress) {
			const deliveryAddressLine1 =
				this.customerDetailsForm.get('deliveryAddressLine1')?.value || '';
			const deliveryAddressLine2 =
				this.customerDetailsForm.get('deliveryAddressLine2')?.value || '';
			const deliveryCity =
				this.customerDetailsForm.get('deliveryCity')?.value || '';
			const deliveryPostcode =
				this.customerDetailsForm.get('deliveryPostcode')?.value || '';
			const deliveryCountry =
				this.customerDetailsForm.get('deliveryCountry')?.value || '';
			deliveryAddressString = [
				deliveryAddressLine1,
				deliveryAddressLine2,
				deliveryCity,
				deliveryPostcode,
				deliveryCountry,
			]
				.filter((part) => part)
				.join(', ');
		}

		if (addressString?.length > 0) {
			this.customerDetailsForm
				.get('address')
				?.setValue(addressString, { emitEvent: false });
		}
		if (deliveryAddressString?.length > 0) {
			this.customerDetailsForm
				.get('deliveryAddress')
				?.setValue(deliveryAddressString, { emitEvent: false });
		}
	}

	submitPostcode() {
		const postcode = this.customerDetailsForm.get('addressPostcodeLookup');
		if (postcode && postcode.valid) {
			this.postcodeLookup.emit(postcode.value);
		}
	}

	submitDeliveryPostcode() {
		const postcode = this.customerDetailsForm.get(
			'deliveryAddressPostcodeLookup'
		);
		if (postcode && postcode.valid) {
			this.deliveryPostcodeLookup.emit(postcode.value);
		}
	}
}
